import {
  Code2,
  Globe2,
  Instagram,
  Mail,
  MessageCircle,
  Music2,
  ServerCog,
  Sparkles,
  TerminalSquare,
  Youtube,
} from 'lucide-react';
import type { ContactLink, Project, ProjectCategory, ServiceTier, Skill, Testimonial } from '../types/portfolio';

export const profile = {
  name: 'SomeTimesAtNight',
  nickname: 'SAD.SomeTime',
  headline: 'Developer for sites, programs and game projects.',
  summary:
    'I make websites, small apps, game projects, and UI for portfolios/services. I code in C#, HTML, CSS, JavaScript, and Python.',
  animatedRoles: ['Site Developer', 'Program Builder', 'Game Developer', 'C# / JS / Python'],
  availability: 'Available for new projects',
};

export const stats = [
  { label: 'years of experience', value: '3+', tone: 'green' },
  { label: 'projects', value: '0+', tone: 'blue' },
  { label: 'clients', value: '0.5+', tone: 'blue' },
  { label: 'programming languages', value: '5', tone: 'red' },
];

export const skills: Skill[] = [
  { name: 'C#', level: 55, group: 'Code' },
  { name: 'HTML', level: 70, group: 'Code' },
  { name: 'CSS', level: 65, group: 'Code' },
  { name: 'JavaScript', level: 55, group: 'Code' },
  { name: 'Python', level: 55, group: 'Code' },
];

export const experience = [
  {
    icon: Code2,
    title: 'Site development',
    text: 'Portfolio pages, service pages, responsive layouts and clean UI blocks.',
  },
  {
    icon: ServerCog,
    title: 'Program logic',
    text: 'Simple tools, app screens, data flows, automations and API-style logic.',
  },
  {
    icon: TerminalSquare,
    title: 'Game projects',
    text: 'Unity/C# prototypes, gameplay states, game UI and project launch pages.',
  },
];

export const projectCategories: ProjectCategory[] = ['All', 'Website', 'Bot', 'Game'];

export const projects: Project[] = [
  {
    id: 'asset-store',
    title: 'Asset Store',
    category: 'Website',
    description:
      'Store-style page for game assets with clean product cards, modern layout and game/dev visual style.',
    tech: ['Game', 'Asset', 'Unity'],
    image: '/project-cloud.svg',
    demo: 'https://assetstore.unity.com/publishers/95337',
    status: 'completed',
  },
  {
    id: 'bot-launcher',
    title: 'Bot Launcher',
    category: 'Bot',
    description:
      'Bot project launcher concept with command blocks, status cards and a simple dashboard-style interface.',
    tech: ['Python', 'Discord API', 'Telegram API'],
    image: '/project-bot.svg',
    demo: 'https://sometimesatnight-portfolio.netlify.app/',
    status: 'available',
  },
  {
    id: 'steam-curator',
    title: 'Steam Curator',
    category: 'Game',
    description:
      'Game/Steam presentation page with project highlights, responsive sections and clean gaming UI.',
    tech: ['Unity', 'C#', 'Steam'],
    image: '/project-unity.svg',
    steam: 'https://store.steampowered.com/curator/45893530',
    demo: 'https://store.steampowered.com/curator/45893530',
    status: 'completed',
  },
  {
    id: 'all-web-site',
    title: 'All Web-Site',
    category: 'Website',
    description:
      'Collection page for website projects built with HTML, CSS and JavaScript, ready for portfolio expansion.',
    tech: ['HTML', 'CSS', 'JavaScript'],
    image: '/project-api.svg',
    demo: 'https://sad-spec-cmyk.github.io/AN-Error/',
    status: 'urgent',
  },
];

export const services: ServiceTier[] = [
  {
    id: 'site',
    name: 'Site',
    price: 'from $50',
    delivery: '2-7 days',
    accent: 'blue',
    features: ['Website', 'Landing', 'Portfolio', 'Responsive UI'],
    models: [
      {
        id: 'site-simple',
        name: 'Simple Website',
        price: '$50',
        note: '1 page',
        features: ['Simple layout', 'Mobile responsive', 'Contact block'],
      },
      {
        id: 'site-intermediate',
        name: 'Intermediate level',
        price: '$100',
        note: '2-4 pages',
        features: ['Multiple sections', 'Animations', 'Better SEO setup'],
      },
      {
        id: 'site-premium',
        name: 'Premium / modern site',
        price: '$250',
        note: 'premium UI',
        features: ['Premium layout', 'Forms', 'Polished effects'],
      },
    ],
  },
  {
    id: 'program',
    name: 'Program',
    price: 'from $100',
    delivery: '5-14 days',
    accent: 'green',
    features: ['Tools', 'Apps', 'Automation', 'Desktop/web logic'],
    models: [
      {
        id: 'program-simple-tool',
        name: 'Simple tool',
        price: '$100',
        note: 'basic utility',
        features: ['One main function', 'Simple UI', 'Setup help'],
      },
      {
        id: 'program-average-desktop',
        name: 'Average desktop app',
        price: '$250',
        note: 'medium app',
        features: ['Multiple screens', 'Saved settings', 'Cleaner workflow'],
      },
      {
        id: 'program-professional',
        name: 'Professional software',
        price: '$2,500',
        note: 'large scope',
        features: ['Advanced architecture', 'Admin logic', 'Launch support'],
      },
    ],
  },
  {
    id: 'game',
    name: 'Game',
    price: 'from $50',
    delivery: '5-25 days',
    accent: 'red',
    features: ['Unity', 'C#', '2D/3D', 'Game UI'],
    models: [
      {
        id: 'game-small-2d',
        name: 'Small 2D game',
        price: '$50',
        note: 'prototype',
        features: ['Basic gameplay', 'Simple UI', 'Build package'],
      },
      {
        id: 'game-unity-3d',
        name: 'Unity 3D project',
        price: '$100',
        note: 'starter 3D',
        features: ['3D scene', 'Player controls', 'Basic interactions'],
      },
      {
        id: 'game-mobile',
        name: 'Mobile game',
        price: '$100',
        note: 'mobile ready',
        features: ['Touch controls', 'Mobile layout', 'Build prep'],
      },
      {
        id: 'game-ultimate',
        name: 'Big Game Ultimate',
        price: '$250',
        note: 'bigger prototype',
        features: ['More systems', 'Levels', 'Polished UI'],
      },
    ],
  },
  {
    id: 'state',
    name: 'State',
    price: 'from $50',
    delivery: '3-10 days',
    accent: 'blue',
    features: ['State logic', 'Status systems', 'Dashboards', 'Workflow'],
    models: [
      {
        id: 'state-simple',
        name: 'Simple state system',
        price: '$50',
        note: 'small flow',
        features: ['Basic statuses', 'Simple UI', 'Clean code'],
      },
      {
        id: 'state-medium',
        name: 'Medium state flow',
        price: '$100',
        note: 'multi-step',
        features: ['Several states', 'Validation', 'Status display'],
      },
      {
        id: 'state-dashboard',
        name: 'State dashboard',
        price: '$250',
        note: 'admin-style',
        features: ['Dashboard cards', 'Filters', 'Order/status logic'],
      },
    ],
  },
];

export const workTypes = [
  'Site',
  'Program',
  'Game',
  'State',
  'Website',
  'Desktop App',
  'Unity Project',
  'Bot',
  'Other',
];

export const featureOptions = [
  'Responsive UI',
  'Contact form',
  'Animations',
  'Stripe Payments',
  'Email Notifications',
  'SEO',
  'PWA',
  'Game UI',
  'State system',
];

export const testimonials: Testimonial[] = [
  {
    name: 'Alex M.',
    role: 'Website client',
    quote: 'Clean layout, fast delivery and the page looked professional.',
    rating: 5,
    avatar: 'AM',
  },
  {
    name: 'Daria K.',
    role: 'UI client',
    quote: 'The design looked modern and was easy to update after launch.',
    rating: 5,
    avatar: 'DK',
  },
  {
    name: 'Max R.',
    role: 'Bot client',
    quote: 'The bot project was organized clearly and worked smoothly.',
    rating: 5,
    avatar: 'MR',
  },
];

export const contacts: ContactLink[] = [
  { label: 'Telegram', value: '@SomeTimesAtNight', href: 'https://t.me/sometimesatnigh', icon: MessageCircle },
  { label: 'Discord', value: 'SomeTimesAtNight Hub', href: 'https://discord.com/invite/6qf256WHkH', icon: Sparkles },
  { label: 'Instagram', value: '@sometimesatnightofficial', href: 'https://www.instagram.com/sometimesatnightofficial/', icon: Instagram },
  { label: 'Email', value: 'sometimesatnightofficial@gmail.com', href: 'mailto:sometimesatnightofficial@gmail.com', icon: Mail },
  { label: 'TikTok', value: '@SomeTimesAtNight', href: 'https://www.tiktok.com/@sometimesatnigh?is_from_webapp=1&sender_device=pc', icon: Music2 },
  { label: 'Website', value: 'SomeTimesAtNight Portfolio', href: 'https://sometimme.netlify.app/', icon: Globe2 },
  { label: 'YouTube', value: 'SomeTimesAtNight', href: 'https://www.youtube.com/@sometimesatnight', icon: Youtube },
  { label: 'Steam', value: 'SomeTimesAtNight', href: 'https://store.steampowered.com/curator/45893530', icon: TerminalSquare },
  { label: 'Itch.io', value: 'SomeTimesAtNight', href: 'https://sometimesatnight.itch.io/', icon: Code2 },
  { label: 'Asset Store', value: 'SomeTimesAtNight', href: 'https://assetstore.unity.com/publishers/95337', icon: ServerCog },
];
